import { ProblemDetailsSchema } from '@clinic/contracts';
import { fa } from '../i18n/fa';

/** A failed API call, carrying the server's registry code and Persian message (RFC 9457 body). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: Record<string, unknown>,
    readonly traceId: string | null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Network failure or offline: nothing reached the server. */
export class NetworkError extends Error {
  constructor() {
    super(fa.app.networkError);
    this.name = 'NetworkError';
  }
}

export async function toApiError(res: Response): Promise<ApiError> {
  try {
    const body = ProblemDetailsSchema.parse(await res.json());
    return new ApiError(res.status, body.code, body.message, body.details, body.traceId);
  } catch {
    return new ApiError(
      res.status,
      'INTERNAL_ERROR',
      fa.app.unknownError,
      {},
      res.headers.get('x-request-id'),
    );
  }
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError || error instanceof NetworkError) return error.message;
  return fa.app.unknownError;
}

/**
 * Every distinct message of a VALIDATION_FAILED response, in order. The response's own message is
 * the general «اطلاعات ارسال‌شده معتبر نیست»; these say what to fix (e.g. «مبلغ بیش از حد مجاز است»).
 */
export function issueMessages(error: unknown): string[] {
  if (!(error instanceof ApiError) || error.code !== 'VALIDATION_FAILED') return [];
  const issues = error.details.issues;
  if (!Array.isArray(issues)) return [];
  const messages = (issues as { message?: unknown }[]).flatMap((i) => (typeof i.message === 'string' ? [i.message] : []));
  return [...new Set(messages)];
}

/** Field-level messages from a VALIDATION_FAILED response, keyed by field path. */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError) || error.code !== 'VALIDATION_FAILED') return {};
  const issues = error.details.issues;
  if (!Array.isArray(issues)) return {};
  const result: Record<string, string> = {};
  for (const issue of issues as { path?: unknown; message?: unknown }[]) {
    if (typeof issue.path === 'string' && typeof issue.message === 'string')
      result[issue.path] = issue.message;
  }
  return result;
}
